import { Button as AntButton, ButtonProps as AntButtonProps } from 'antd';
import { forwardRef } from 'react';

type MuiVariant = 'contained' | 'outlined' | 'text' | 'filled' | 'dashed' | 'link';
type MuiColor = 'primary' | 'secondary' | 'info' | 'error' | 'inherit' | 'success' | 'warning' | string;
type MuiSize = 'small' | 'medium' | 'large';
type HtmlButtonType = 'submit' | 'button' | 'reset';
type AntVisualType = NonNullable<AntButtonProps['type']>;

const antVisualTypes = new Set<string>(['primary', 'default', 'dashed', 'link', 'text']);
const htmlButtonTypes = new Set<string>(['submit', 'button', 'reset']);

export interface ButtonProps extends Omit<AntButtonProps, 'type' | 'color' | 'variant' | 'size'> {
  variant?: MuiVariant;
  color?: MuiColor;
  size?: MuiSize | AntButtonProps['size'];
  type?: AntVisualType | HtmlButtonType;
  disableElevation?: boolean;
  disableRipple?: boolean;
  disableFocusRipple?: boolean;
  disableTouchRipple?: boolean;
}

function resolveVisualType(variant: MuiVariant | undefined, type: ButtonProps['type']): AntVisualType {
  if (typeof type === 'string' && antVisualTypes.has(type)) {
    return type as AntVisualType;
  }

  switch (variant) {
    case 'contained':
    case 'filled':
      return 'primary';
    case 'outlined':
      return 'default';
    case 'dashed':
      return 'dashed';
    case 'link':
      return 'link';
    case 'text':
      return 'text';
    default:
      return 'text';
  }
}

function resolveHtmlType(type: ButtonProps['type']): HtmlButtonType {
  if (typeof type === 'string' && htmlButtonTypes.has(type)) {
    return type as HtmlButtonType;
  }

  return 'button';
}

function resolveSize(size: ButtonProps['size']): AntButtonProps['size'] {
  if (size === 'medium') {
    return 'middle';
  }

  if (size === 'small' || size === 'large' || size === 'middle') {
    return size;
  }

  return 'middle';
}

function buildButtonClassName({
  visualType,
  color,
  disableElevation,
  className,
}: {
  visualType: AntVisualType;
  color?: MuiColor;
  disableElevation?: boolean;
  className?: string;
}) {
  const isDanger = color === 'error';
  const isInfo = color === 'info';

  return [
    'font-satoshi-medium inline-flex items-center justify-center shadow-none!',
    visualType === 'primary' || visualType === 'default' || visualType === 'text' ? 'uppercase' : '',
    disableElevation ? 'shadow-none!' : '',
    visualType === 'primary'
      ? 'border-brand! bg-brand! text-white! hover:border-brand! hover:bg-brand! hover:text-white! disabled:border-dust! disabled:bg-dust! disabled:text-white!'
      : '',
    visualType === 'default'
      ? 'border-dust! bg-white! text-ash! hover:border-ash! hover:bg-white! hover:text-ash! focus:border-brand! focus:text-brand! disabled:border-dust! disabled:text-dust!'
      : '',
    visualType === 'dashed'
      ? 'border-dust! bg-white! text-ash! hover:border-ash! hover:text-ash! focus:border-brand! focus:text-brand! disabled:border-dust! disabled:text-dust!'
      : '',
    visualType === 'text' && isDanger ? 'text-red-600! hover:bg-red-50! hover:text-red-600!' : '',
    visualType === 'text' && isInfo ? 'text-ash! hover:bg-fog! hover:text-ash!' : '',
    visualType === 'text' && !isDanger && !isInfo
      ? 'text-ash! hover:bg-fog! hover:text-ash! focus:text-brand! disabled:text-dust!'
      : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
}

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(function Button(
  {
    variant,
    color,
    size = 'medium',
    type,
    disableElevation,
    disableRipple: _disableRipple,
    disableFocusRipple: _disableFocusRipple,
    disableTouchRipple: _disableTouchRipple,
    className,
    danger,
    htmlType,
    children,
    ...props
  },
  ref,
) {
  void _disableRipple;
  void _disableFocusRipple;
  void _disableTouchRipple;

  const visualType = resolveVisualType(variant, type);
  const resolvedHtmlType = htmlType ?? resolveHtmlType(type);
  const isDanger = danger || color === 'error';

  return (
    <AntButton
      {...props}
      ref={ref}
      type={visualType}
      size={resolveSize(size)}
      htmlType={resolvedHtmlType}
      danger={isDanger}
      className={buildButtonClassName({
        visualType,
        color,
        disableElevation,
        className,
      })}
    >
      {children}
    </AntButton>
  );
});
